/**
 * Feriados nacionais brasileiros.
 *
 * Usado pelos calendários de frequência para impedir o lançamento em dia
 * sem aula. Só entram os feriados nacionais previstos em lei — Carnaval e
 * Corpus Christi são ponto facultativo, não feriado, e cada rede decide se
 * tem aula, então continuam liberados. Feriados estaduais e municipais
 * também não entram (variam por cidade).
 */

// Feriados de data fixa: 'MM-DD' -> nome
const FERIADOS_FIXOS = {
  '01-01': 'Confraternização Universal',
  '04-21': 'Tiradentes',
  '05-01': 'Dia do Trabalho',
  '09-07': 'Independência do Brasil',
  '10-12': 'Nossa Senhora Aparecida',
  '11-02': 'Finados',
  '11-15': 'Proclamação da República',
  '11-20': 'Dia Nacional de Zumbi e da Consciência Negra',
  '12-25': 'Natal',
};

// Cache por ano — o cálculo da Páscoa é o único trecho mais caro.
const cachePorAno = new Map();

const doisDigitos = (n) => String(n).padStart(2, '0');

/**
 * Domingo de Páscoa do ano (algoritmo de Meeus/Jones/Butcher).
 * Retorna { mes, dia } com mês em base 1.
 */
function calcularPascoa(ano) {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return { mes, dia };
}

/** Soma dias a uma data (base 1 no mês) e devolve 'MM-DD'. */
function deslocar(ano, mes, dia, deslocamento) {
  const data = new Date(Date.UTC(ano, mes - 1, dia + deslocamento));
  return `${doisDigitos(data.getUTCMonth() + 1)}-${doisDigitos(data.getUTCDate())}`;
}

/**
 * Feriados nacionais do ano: { 'YYYY-MM-DD': 'Nome do feriado' }.
 */
export function getFeriadosNacionais(ano) {
  if (cachePorAno.has(ano)) return cachePorAno.get(ano);

  const feriados = {};
  Object.entries(FERIADOS_FIXOS).forEach(([mmdd, nome]) => {
    feriados[`${ano}-${mmdd}`] = nome;
  });

  // Sexta-feira Santa: dois dias antes do Domingo de Páscoa.
  const pascoa = calcularPascoa(ano);
  feriados[`${ano}-${deslocar(ano, pascoa.mes, pascoa.dia, -2)}`] = 'Sexta-feira Santa';

  cachePorAno.set(ano, feriados);
  return feriados;
}

/**
 * Nome do feriado nacional de uma data 'YYYY-MM-DD', ou null se for dia comum.
 */
export function getFeriadoNacional(dataISO) {
  if (!dataISO || typeof dataISO !== 'string') return null;
  const ano = Number(dataISO.slice(0, 4));
  if (!Number.isInteger(ano)) return null;
  return getFeriadosNacionais(ano)[dataISO] || null;
}

/** Atalho booleano para `getFeriadoNacional`. */
export function isFeriadoNacional(dataISO) {
  return getFeriadoNacional(dataISO) !== null;
}
